import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'; // Import DomSanitizer

export type ButtonState = 'initial' | 'loading' | 'success';

@Component({
  selector: 'app-loader',
  templateUrl: './app-loader.component.html',
  styleUrls: ['./app-loader.component.scss'],
})
export class AppLoaderComponent implements OnInit, OnDestroy {
  private _color?: string;
  private _Tcolor?: string;
  private _gradColor?: string;
  private _gradTcolor?: string;

  @Input() loaderText?: string;
  @Input() iconSrc?: string;
  @Input() top: number = 50;

  @Input()
  set color(value: string | undefined) {
    this._color = value;
    this._Tcolor = value;
    this._gradColor = value;
    this._gradTcolor = value;
  }
  @Input() isActive: boolean = false;

  @HostBinding('style.--loader-color')
  get LoaderColor(): string | null {
    return this._color ? `var(--ion-color-${this._color})` : `var(--ion-color-primary)`;
  }

  @HostBinding('style.--loader-Tcolor')
  get LoaderTColor(): string | null {
    return this._Tcolor ? `var(--ion-darker-${this._Tcolor})` : `var(--ion-darker-primary)`;
  }
  @HostBinding('style.--loader-shadeDark')
  get gradColor(): string | null {
    return this._gradColor ? `var(--ion-white-${this._gradColor})` : `var(--ion-white-primary)`;
  }

  @HostBinding('style.--loader-pass')
  get gradTColor(): string | null {
    return this._gradTcolor ? `var(--ion-white-${this._gradTcolor})` : `var(--ion-white-primary)`;
  }

  @ViewChild('animContainer', { static: false }) svgContainerRef!: ElementRef;

  private isSvgLoaded: boolean = false;
  private animationInterval: any;

  constructor(private cdRef: ChangeDetectorRef, private sanitizer: DomSanitizer) { } // Inject DomSanitizer

  ngOnInit() {
    // ngOnInit runs once when component is initialized.
    // We'll rely on ionViewWillEnter for SVG setup on subsequent visits.
  }

  ionViewWillEnter() {
    if (this.isActive && !this.isSvgLoaded) {
      setTimeout(() => {
        if (this.svgContainerRef && this.svgContainerRef.nativeElement) {
          this.setupFibonacciConcentric();
          this.isSvgLoaded = true;
        } else {
          console.warn("SVG container not found after timeout, re-trying setup.");
          // Optionally, add more robust checks or retry logic if needed
        }
      }, 50); // A small delay, adjust if necessary
    } else if (!this.isActive && this.isSvgLoaded) {
      // If isActive becomes false, clear the SVG
      this.clearSvg();
    }
  }

  ionViewWillLeave() {
    this.clearSvg(); // Clear SVG when leaving the view
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']) {
      if (this.isActive && !this.isSvgLoaded) {
        this.ionViewWillEnter(); // Trigger setup if isActive becomes true
      } else if (!this.isActive && this.isSvgLoaded) {
        this.clearSvg(); // Clear if isActive becomes false
      }
    }
  }

  ngOnDestroy() {
    this.clearSvg(); // Ensure cleanup if component is truly destroyed
  }

  private clearSvg() {
    if (this.svgContainerRef && this.svgContainerRef.nativeElement) {
      this.svgContainerRef.nativeElement.innerHTML = "";
    }
    this.isSvgLoaded = false;
    // Clear any intervals or timeouts initiated by setupFibonacciConcentric if they exist
    if (this.animationInterval) {
      clearInterval(this.animationInterval); // Or clearTimeout
      this.animationInterval = null;
    }
  }

  genStyle(): { [key: string]: string } {
    if (this.color) {
      const color = `var(--ion-text-${this.color})`
      const top = `${this.top}%`;
      return { 'color': color, 'top': top };
    }
    return {}; // Return empty object if no color is provided
  }

  convertToPixels(value: string): number {
    if (value.endsWith('vh')) {
      const vh = parseFloat(value);
      return (vh / 100) * window.innerHeight;
    } else if (value.endsWith('vw')) {
      const vw = parseFloat(value);
      return (vw / 100) * window.innerWidth;
    } else {
      return parseFloat(value); // assume it's already in px
    }
  }


  setupFibonacciConcentric() {
    const container = this.svgContainerRef.nativeElement;
    if (!container) {
      console.error("SVG container (animContainer) is null. Cannot setup animation.");
      return;
    }

    container.innerHTML = ""; // clear previous content if any

    const N = 600;
    const SIZE = this.convertToPixels("40vw");
    const SIZEH = this.convertToPixels("100vh");
    const DOT_RADIUS = 1;
    const MARGIN = 0;
    const CENTER = SIZE / 2;
    const MAX_RADIUS = CENTER - MARGIN - DOT_RADIUS;
    const GOLDEN_ANGLE = Math.PI * (5 - Math.sqrt(150));
    const DURATION = 6;
    const svgNS = "http://www.w3.org/2000/svg";

    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("width", `${SIZE}`);
    svg.setAttribute("height", `${SIZE}`);
    svg.setAttribute("viewBox", `0 0 ${SIZE} ${SIZE / 1.2}`);
    svg.setAttribute("xmlns", svgNS);
    container.appendChild(svg);

    // const defs = document.createElementNS(svgNS, "defs"); // Define defs
    // const pattern = document.createElementNS(svgNS, "pattern"); // Define pattern
    // const image = document.createElementNS(svgNS, "image"); // Define image

    // const uniquePatternId = `imagePattern-${Math.random().toString(36).substr(2, 9)}`;

    // pattern.setAttribute("id", uniquePatternId);
    // pattern.setAttribute("x", "0");
    // pattern.setAttribute("y", "0");
    // pattern.setAttribute("width", "100%");
    // pattern.setAttribute("height", "100%");
    // pattern.setAttribute("viewBox", "0 0 100 100");
    // pattern.setAttribute("preserveAspectRatio", "xMidYMid slice");

    // image.setAttribute("x", "0");
    // image.setAttribute("y", "0");
    // image.setAttribute("width", "100");
    // image.setAttribute("height", "100");
    // image.setAttribute("href", "assets/svgIcon.png"); // Set your image path

    // pattern.appendChild(image);
    // defs.appendChild(pattern); // Append pattern to defs
    // svg.appendChild(defs); // Append defs to svg


    const g = document.createElementNS(svgNS, "g");

    // Group rotation animation
    // const groupAnim = document.createElementNS(svgNS, "animateTransform");
    // groupAnim.setAttribute("attributeName", "transform");
    // groupAnim.setAttribute("attributeType", "XML");
    // groupAnim.setAttribute("type", "rotate");
    // groupAnim.setAttribute("from", `0 ${CENTER} ${CENTER}`);
    // groupAnim.setAttribute("to", `-360 ${CENTER} ${CENTER}`);
    // groupAnim.setAttribute("dur", `${DURATION * 5}s`);
    // groupAnim.setAttribute("repeatCount", "indefinite");
    // groupAnim.setAttribute("calcMode", "spline");
    // groupAnim.setAttribute("keySplines", "0.5 1 0.5 1;1 0.5 1 0.5");
    // g.appendChild(groupAnim);


    for (let i = 10; i < N*1.1; i++) {
      const idx = i * 1.1 ;
      const frac = (idx / N) / 2;
      const r = Math.sqrt(frac) * MAX_RADIUS;
      const theta = idx * GOLDEN_ANGLE;
      const x = (CENTER) - ((r) * Math.sin(theta));
      const y = (CENTER) - ((r) * Math.cos(theta));
      const xf = (CENTER) + ((r) * Math.sin(theta));
      const yf = (CENTER) + ((r) * Math.cos(theta));

      if (i == (N*1.2) - 1) {
     
        // const innerRing = document.createElementNS(svgNS, "circle");
        // innerRing.setAttribute("cx", `${SIZE / 2}`);
        // innerRing.setAttribute("cy", `${(SIZE) / 2}`);
        // innerRing.setAttribute("r", '10');
        // innerRing.setAttribute("fill", `url(#${uniquePatternId})`); 
        // innerRing.setAttribute("stroke", `var(--loader-Tcolor)`);
        // innerRing.setAttribute("stroke-width", `0`);
        // innerRing.setAttribute("opacity", "0");
        // innerRing.setAttribute("scale", "1");
        // innerRing.setAttribute("transform-origin", "50%,50%");
        // innerRing.classList.add('centered-svg-element'); // Add the CSS class


        // const animCStroke = document.createElementNS(svgNS, "animate");
        // animCStroke.setAttribute("attributeName", "stroke-width");
        // animCStroke.setAttribute("values", "0;0;0;5;4;3;0");
        // animCStroke.setAttribute("dur", `${DURATION}s`);
        // animCStroke.setAttribute("begin", `${(frac * DURATION).toFixed(2)}s`);
        // animCStroke.setAttribute("repeatCount", "indefinite");
        // animCStroke.setAttribute("calcMode", "spline");
        // animCStroke.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");


        // animCScale.setAttribute("calcMode", "spline");
        // animCScale.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");

        // const animCOpacity = document.createElementNS(svgNS, "animate");
        // animCOpacity.setAttribute("attributeName", "opacity");
        // animCOpacity.setAttribute("values", "0;0.5;1;1;1;0.5;0");
        // animCOpacity.setAttribute("dur", `${DURATION}s`);
        // animCOpacity.setAttribute("begin", `${(frac * DURATION).toFixed(2)}s`);
        // animCOpacity.setAttribute("repeatCount", "indefinite");
        // animCOpacity.setAttribute("calcMode", "spline");
        // animCOpacity.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");
        
      //  const animCRotate = document.createElementNS(svgNS, "animateTransform");
      //     animCRotate.setAttribute("attributeName", "transform");
      //     animCRotate.setAttribute("attributeType", "XML");
      //     animCRotate.setAttribute("type", "rotate");
      //     animCRotate.setAttribute("from", `0 ${CENTER} ${CENTER}`);
      //     animCRotate.setAttribute("to", `0 ${CENTER} ${CENTER}`);
      //     animCRotate.setAttribute("dur", `${DURATION * 0}s`);
      //     animCRotate.setAttribute("repeatCount", "indefinite");
      //     // animCRotate.setAttribute("calcMode", "spline");
      //     // animCRotate.setAttribute("keySplines", "0.5 1 0.5 1;1 0.5 1 0.5");

      //   innerRing.appendChild(animCScale);
      //   // innerRing.appendChild(animCOpacity);
      //   innerRing.appendChild(animCRotate);
      //   // innerRing.appendChild(animCStroke);
      //   g.appendChild(innerRing);
      }

      const circle = document.createElementNS(svgNS, "circle");
      circle.setAttribute("cx", x.toFixed(2));
      circle.setAttribute("cy", y.toFixed(2));
      circle.setAttribute("r", (DOT_RADIUS).toString());
      circle.setAttribute("fill", `var(--loader-Tcolor)`);
      circle.setAttribute("opacity", "0");
      circle.setAttribute("scale", "0");
      circle.setAttribute("transform-origin", "50% 50%");

      const animR = document.createElementNS(svgNS, "animate");
      animR.setAttribute("attributeName", "r");
      animR.setAttribute("values", `${DOT_RADIUS * 1};${DOT_RADIUS * 0.5};${DOT_RADIUS * 1.5};${DOT_RADIUS * 0.1};${DOT_RADIUS * 1}`);
      animR.setAttribute("dur", `${DURATION}s`);
      animR.setAttribute("begin", `${(frac - DURATION).toFixed(2)}s`);
      animR.setAttribute("repeatCount", "indefinite");
      
      // animR.setAttribute("calcMode", "spline");
      // animR.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");

      const animO = document.createElementNS(svgNS, "animate");
      animO.setAttribute("attributeName", "opacity");
      animO.setAttribute("values", "1;0;1;0.1;1");
      animO.setAttribute("dur", `${DURATION}s`);
      animO.setAttribute("begin", `${(frac - DURATION).toFixed(2)}s`);
      animO.setAttribute("repeatCount", "indefinite");
      // animO.setAttribute("calcMode", "spline");
      // animO.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");

    const animRR = document.createElementNS(svgNS, "animate");
    animRR.setAttribute("attributeName", "cy");
    animRR.setAttribute("values", `${yf};${x};${yf};${xf};${y};${x};${y};${yf}`);
    animRR.setAttribute("dur", `${DURATION}s`);
    animRR.setAttribute("begin", `${(frac - (DURATION)).toFixed(2)}s`);
    animRR.setAttribute("repeatCount", "indefinite");
    // animRR.setAttribute("additive", "sum");
const animXX = document.createElementNS(svgNS, "animate");
    animXX.setAttribute("attributeName", "cx");
    animXX.setAttribute("values", `${x};${yf};${x};${y};${xf};${y};${x};${x}`);
    animXX.setAttribute("dur", `${DURATION}s`);
    animXX.setAttribute("begin", `${(frac - (DURATION)).toFixed(2)}s`);
    animXX.setAttribute("repeatCount", "indefinite");
    // animXX.setAttribute("additive", "sum");
      const animS = document.createElementNS(svgNS, "animateTransform");
      animS.setAttribute("attributeName", "transform");
      animS.setAttribute("attributeType", "XML");
      animS.setAttribute("type", "scale");
      animS.setAttribute("begin", `${(frac - DURATION).toFixed(2)}s`);
      animS.setAttribute("values", "1;0.7;1;1.3;1");
      animS.setAttribute("dur", `${DURATION}s`);
      animS.setAttribute("repeatCount", "indefinite");
      // animS.setAttribute("accumulate", "sum");
      // animS.setAttribute("calcMode", "spline");
      // animS.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");
               const animCScale = document.createElementNS(svgNS, "animate");
        animCScale.setAttribute("attributeName", "fill");
        animCScale.setAttribute("from", "var(--loader-color)");
        animCScale.setAttribute("to", "var(--loader-Tcolor)");
        animCScale.setAttribute("dur", `${DURATION}s`);
        animCScale.setAttribute("begin", `${(frac * DURATION).toFixed(2)}s`);
        animCScale.setAttribute("repeatCount", "indefinite");
        // animCScale.setAttribute("additive", "replace");

      circle.appendChild(animR);
      circle.appendChild(animCScale);
      circle.appendChild(animO);
      circle.appendChild(animRR);
      circle.appendChild(animXX);
      circle.appendChild(animS);

      g.appendChild(circle);


    }

    // const animO = document.createElementNS(svgNS, "animate");
    // animO.setAttribute("attributeName", "opacity");
    // animO.setAttribute("values", "0;0.1;1;0.1;1;0.5;0");
    // animO.setAttribute("dur", `${DURATION}s`);
    // animO.setAttribute("begin", `${(DURATION).toFixed(2)}s`);
    // animO.setAttribute("repeatCount", "indefinite");
    // animO.setAttribute("calcMode", "spline");
    // animO.setAttribute("keySplines", "0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1");



    svg.appendChild(g);
  }
  //  getGoldenSpiralParticles(N: number, MAX_RADIUS: number, CENTER: number) {
//   const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
//   const points: { x: number; y: number }[] = [];

//   for (let i = 0; i < N; i++) {
//     const idx = i + 0.4;
//     const frac = idx / N;
//     const r = Math.sqrt(frac) * MAX_RADIUS;
//     const theta = idx * GOLDEN_ANGLE;
//     const x = CENTER - r * Math.cos(theta);
//     const y = CENTER - r * Math.sin(theta);
//     points.push({ x, y });
//   }

//   return points;
// }

// generateSineWavePath(
//   width: number,
//   height: number,
//   frequency: number = 2,
//   amplitude: number = 40,
//   step: number = 5
// ): string {
//   const points: string[] = [];
//   for (let x = 0; x <= width; x += step) {
//     const y = height / 2 + amplitude * Math.sin((x / width) * frequency * Math.PI * 2);
//     points.push(`${x},${y}`);
//   }
//   return `M${points[0]} L${points.slice(1).join(" ")}`;
// }

//  generateRadialLines(count: number, centerX: number, centerY: number, radius: number) {
//   const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];

//   for (let i = 0; i < count; i++) {
//     const angle = (2 * Math.PI * i) / count;
//     const x2 = centerX + radius * Math.cos(angle);
//     const y2 = centerY + radius * Math.sin(angle);
//     lines.push({ x1: centerX, y1: centerY, x2, y2 });
//   }

//   return lines;
// }

//  generateCircularPath(cx: number, cy: number, r: number): string {
//   return `M ${cx + r}, ${cy} 
//           A ${r},${r} 0 1,0 ${cx - r},${cy} 
//           A ${r},${r} 0 1,0 ${cx + r},${cy}`;
// }
}